import { useEffect, useMemo, useRef, useState } from "react"
import { useMutation, useQuery } from "react-query"
import { postApi, postApiWithToken } from "../../../core/services/fetch-api/post"
import { useSearchParams } from "react-router-dom"
import { deformatNumber, formatNumber, useMainContext } from "../../../modules/staking"
import { useSocketContent } from "../../../core/contexts/socket-content"
import { useProfileQuery } from "../../../core/services/react-query/useProfileQuery"
import { normalFetch } from "../../../core/services/fetch-api/get"
import { useQueryContext } from "../../../core/contexts/query"

const initialPayload = { fromCurrency: "", toCurrency: "", amount: 0 }

const useSwapInvoiceMutation = (onSuccess) => {
    const {
        profile: { token },
    } = useMainContext()
    return useMutation(
        (data) => postApiWithToken(data, token, 'swaps/invoice'),
        { onSuccess },
    )
}

const useCreateSwapMutation = (onSuccess) => {
    const {
        profile: { token },
    } = useMainContext()
    return useMutation(
        (data) => postApiWithToken(data, token, 'swaps'),
        { onSuccess }
    )
}

export const useGetSwapSettings = () => {
    const {
        profile: { token },
    } = useMainContext()
    return useQuery('swap-settings', () => normalFetch(token, 'settings/swap'), {
        select: (res) => res?.data?.data,
    })
}

export const useSwap = (marketCoin) => {
    const [params] = useSearchParams()
    const preCoin = params.get("coin")
    const [invoiceModal, setInvoiceModal] = useState({ open: false, data: null })
    const closeInvoiceModal = () => setInvoiceModal({ open: false, data: null })

    const {
        profile: { token },
    } = useMainContext()
    const { queryClient, setToast } = useQueryContext()

    const { prices: _prices } = useSocketContent()
    const [pricesList, setPricesList] = useState([])
    useEffect(() => {
        setPricesList(_prices)
    }, [_prices])

    const { data: profile, isLoading: profileLoading } = useProfileQuery()
    const { data: swapSettings, isLoading: loadingSwapSetting } = useGetSwapSettings()

    // Build allowed currency list from swap settings
    const allowedCurrencies = useMemo(
        () => swapSettings?.currencies?.map((c) => c.currency?.toLowerCase()) || [],
        [swapSettings]
    )

    // Form & selection
    const [payload, setPayload] = useState(initialPayload)
    const [coin, setCoin] = useState()

    // Price & rate
    const [rate, setRate] = useState(0)
    const [change, setChange] = useState(0)

    const invoiceMutation = useSwapInvoiceMutation((res) => {
        setInvoiceModal({ open: true, data: res.data.data })
    })
    const createSwapMutation = useCreateSwapMutation(() => {
        setInvoiceModal({ open: false, data: null })
        setPayload(initialPayload)
        queryClient.invalidateQueries('fetch-profile')
    })

    /** Load coin from params or default */
    const initialCoinLoaded = useRef(false)
    useEffect(() => {
        if (!!preCoin && pricesList?.length && !coin) {
            const exist = pricesList.find((item) => item.id === preCoin)
            if (exist) setCoin(exist)
        }
    }, [pricesList])

    useEffect(() => {
        if (pricesList?.length && !initialCoinLoaded.current && !preCoin) {
            initialCoinLoaded.current = true
            setCoin(pricesList[0])
        }
    }, [pricesList])

    /** Update rate from socket */
    useEffect(() => {
        if (coin) {
            const newPrice = pricesList.find((x) => x.id === coin.id)
            if (newPrice) {
                setRate(newPrice.sell) // or buy, depending on from/to logic
                setChange(newPrice?.changes?.["24h"]?.changePresent || 0)
            }
        }
    }, [pricesList, coin])

    /** Update form fields */
    const updatePayload = (field, value) => {
        setPayload((prev) => ({
            ...prev,
            [field]: value,
        }))
    }

    /** Get invoice from API */
    const getInvoice = () => {
        if (!payload.fromCurrency || !payload.toCurrency || !payload.amount) return

        if (payload.fromCurrency.id === payload.toCurrency.id) {
            setToast({ isError: true, show: true, message: "swap-same-coin-err" })
            return
        }

        // validate user login & balance like OTC
        if (!token || !profile) {
            // trigger login modal
            return
        }
        invoiceMutation.mutate({
            fromCurrency: payload.fromCurrency.id,
            toCurrency: payload.toCurrency.id,
            amount: deformatNumber(payload.amount),

        })
    }

    /** Confirm swap */
    const confirmSwap = () => {
        if (!invoiceModal.data) return
        createSwapMutation.mutate({
            fromCurrency: payload.fromCurrency.id,
            toCurrency: payload.toCurrency.id,
            amount: deformatNumber(payload.amount),

        })
    }

    /** Balance info like OTC */
    const balance = useMemo(() => {
        let b = "0"
        if (profile && payload.fromCurrency) {
            if (payload.fromCurrency.id === "irt")
                b = `${formatNumber(profile.balance)} تومان`
            else
                b = `${formatNumber(profile.coins.find((i) => i.coin === payload.fromCurrency.id)?.amount) || 0} ${payload.fromCurrency.id.toUpperCase()}`
        }
        return b
    }, [profile, payload.fromCurrency])

    useEffect(() => {
        if (marketCoin && marketCoin.id !== payload.toCurrency?.id) {
            updatePayload('fromCurrency', marketCoin)
        }
    }, [marketCoin])

    return {
        payload,
        updatePayload,
        coin,
        setCoin,
        rate,
        change,
        balance,
        getInvoice,
        confirmSwap,
        invoiceModal,
        closeInvoiceModal,
        allowedCurrencies,
        isInvoiceLoading: invoiceMutation.isLoading,
        isSwapLoading: createSwapMutation.isLoading,
        isProfileLoading: profileLoading
    }
}
